/**
 * The inventory dashboard's snapshot selection, shared by its widgets so they all show the same
 * snapshot. The KPI tiles read the levels-chart point the picker selects instead of running a
 * query of their own, and the picker only ever holds a snapshot the list offers: the one the API
 * anchors the department and stock-out widgets on.
 */

/**
 * One point of the levels series: a snapshot, whose `date` is its value in the snapshot picker
 * (yyyy-MM-dd), or the live reading, whose `date` is {@link LIVE}. The `lm` and `ly` figures are
 * the snapshots it is compared with: the month before and the same month a year earlier.
 */
export interface InventoryLevelPoint {
    date: string
    period: string
    totalQuantity: number
    uniqueSKUs: number
    inventoryValue: number
    lmTotalQuantity: number
    lmUniqueSKUs: number
    lmInventoryValue: number
    lyTotalQuantity: number
    lyUniqueSKUs: number
    lyInventoryValue: number
}

export interface SnapshotOption {
    date: string
    label: string
}

/** The snapshot picker's value for the live StoreInv table. */
export const LIVE = 'LIVE'

const DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * The snapshot to show for `selected`, from `options` (newest first): `selected` itself when the
 * list offers it; otherwise the latest snapshot taken on or before that day, which is how the API
 * resolves a day; otherwise the first option, Live.
 */
export function resolveSnapshot(selected: string, options: SnapshotOption[]): string {
    if (options.length === 0 || options.some(o => o.date === selected)) return selected
    // yyyy-MM-dd dates order as strings.
    const onOrBefore = DAY.test(selected)
        ? options.find(o => o.date !== LIVE && o.date <= selected)
        : undefined
    return (onOrBefore ?? options[0]).date
}

/** A yyyy-MM period moved by whole months. */
function shiftPeriod(period: string, months: number): string {
    const [y, m] = period.split('-').map(Number)
    const d = new Date(y, m - 1 + months, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * The figures the KPI tiles show for the selected snapshot: its point in the levels series, the
 * same numbers the chart plots and its tooltip compares. A snapshot where nothing matches the
 * filters has no point. It reads as zero, compared with the series' points for the month before
 * and the same month a year earlier, the snapshots the API compares a point with. Undefined for
 * Live when the series has no live point.
 */
export function snapshotFigures(levels: InventoryLevelPoint[], selected: string): InventoryLevelPoint | undefined {
    const point = levels.find(pt => pt.date === selected)
    if (point || !DAY.test(selected)) return point

    const period = selected.slice(0, 7)
    const prior = levels.find(pt => pt.period === shiftPeriod(period, -1))
    const lastYear = levels.find(pt => pt.period === shiftPeriod(period, -12))
    return {
        date: selected,
        period,
        totalQuantity: 0,
        uniqueSKUs: 0,
        inventoryValue: 0,
        lmTotalQuantity: prior?.totalQuantity ?? 0,
        lmUniqueSKUs: prior?.uniqueSKUs ?? 0,
        lmInventoryValue: prior?.inventoryValue ?? 0,
        lyTotalQuantity: lastYear?.totalQuantity ?? 0,
        lyUniqueSKUs: lastYear?.uniqueSKUs ?? 0,
        lyInventoryValue: lastYear?.inventoryValue ?? 0,
    }
}
